import { detectUnreadableFileContainer } from './detectUnreadableFileContainer'

export const MAX_UPLOAD_FILE_SIZE_BYTES = 30 * 1024 * 1024
export const MAX_UPLOAD_FILE_SIZE_MB = 30

export class UploadTooLargeError extends Error {
  readonly limitBytes: number

  constructor(limitBytes: number = MAX_UPLOAD_FILE_SIZE_BYTES) {
    super(`Upload exceeds the maximum file size of ${MAX_UPLOAD_FILE_SIZE_MB} MB`)
    this.name = 'UploadTooLargeError'
    this.limitBytes = limitBytes
  }
}

export class PasswordProtectedFileError extends Error {
  constructor() {
    super('Upload blocked: password-protected file')
    this.name = 'PasswordProtectedFileError'
  }
}

export class RightsProtectedFileError extends Error {
  constructor() {
    super('Upload blocked: rights-protected file')
    this.name = 'RightsProtectedFileError'
  }
}

export class InvalidPdfFileError extends Error {
  constructor() {
    super('Upload blocked: file is not a valid PDF')
    this.name = 'InvalidPdfFileError'
  }
}

export class InvalidLegacyXlsFileError extends Error {
  constructor() {
    super('Upload blocked: file is not a valid Excel 97–2003 workbook')
    this.name = 'InvalidLegacyXlsFileError'
  }
}

export function isUploadTooLargeError(error: unknown): error is UploadTooLargeError {
  return error instanceof UploadTooLargeError
}

export function isPasswordProtectedFileError(
  error: unknown
): error is PasswordProtectedFileError {
  return error instanceof PasswordProtectedFileError
}

export function isRightsProtectedFileError(
  error: unknown
): error is RightsProtectedFileError {
  return error instanceof RightsProtectedFileError
}

export function isInvalidPdfFileError(error: unknown): error is InvalidPdfFileError {
  return error instanceof InvalidPdfFileError
}

export function isInvalidLegacyXlsFileError(
  error: unknown
): error is InvalidLegacyXlsFileError {
  return error instanceof InvalidLegacyXlsFileError
}

export function assertUploadFileSize(file: File): void {
  if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
    throw new UploadTooLargeError()
  }
}

export async function assertUploadFileReadable(file: File): Promise<void> {
  const detection = await detectUnreadableFileContainer(file)
  if (detection.kind === 'invalid-legacy-xls') {
    throw new InvalidLegacyXlsFileError()
  }
  if (detection.kind === 'invalid-pdf') {
    throw new InvalidPdfFileError()
  }
  if (detection.kind === 'rights-protected') {
    throw new RightsProtectedFileError()
  }
  if (detection.kind === 'encrypted') {
    throw new PasswordProtectedFileError()
  }
}
